import React, { Fragment, useState, useMemo, useCallback, useRef } from "react";
import { DateTime } from "luxon";
import IconWait from "../iconComponents/IconWait";
import IconCancelled from "../iconComponents/IconCancelled";
import IconTick from "../iconComponents/IconTick";
import IconCrayon from "../iconComponents/Crayon";
import IconWarning from "../iconComponents/IconWarning";
import IconIndicator from "../iconComponents/Indicator";

export function formatAddress(address) {
    // Helper function for taking in all address components, adding a comma between them and generating an address

    return address.map( (item, id) => { 
        return <Fragment key={id}>
                    {item.reduce((part1, part2) => part1 === null ? [part2] : [part1, ', ', part2])}<br/>
               </Fragment> 
    })
}

// export function formatDateString(date) {
//     // Helper function that formats a date string
//     return date.substring(0,4).concat("/", date.substring(5,7), "/", date.substring(8,10));
// }

export function numberWithCommas(x) {
    if(x !== null && x !== '') {
        return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
     }else{
        return x;
     }
}

// export function formatShortDate(date) {
//      // Helper function using Luxon for taking in a date from Postgresql (2020-01-01T00:00:00-05:00) and formatting it to Jan 01, 2020
//     const _date = DateTime.fromISO(date);
//     return _date.toLocaleString(DateTime.DATE_MED);
// }

// export function formatDate(date) {
//     // Helper function using Luxon for taking in a date from Postgresql (2020-01-01T00:00:00-05:00) and formatting it to YYYY/MM/DD
//     const _date = DateTime.fromISO(date);
//     return _date.toFormat('yyyy/MM/dd');
// }

export function daysBetween(date1, date2) {
     // Helper function using Luxon for taking in 2 dates from Postgresql (2020-01-01T00:00:00-05:00) and datermining length of days between them
    var start = DateTime.fromISO(date1);
    var end = DateTime.fromISO(date2);
    var diffInDays = end.diff(start, ['days', 'hours', 'minutes'])
    // return diffInDays;
    return diffInDays.toObject(); 
}

export const formatDateTimeToZone = (date, zone) => {
    // take in date from back end with GMT time in ISO format (eg "2021-09-17T21:17:23.371Z")
    // take in zone (optional) if user has a chosen a zone to override local device, in luxon format (eg "Europe/London"). If no zone is passed, local device zone is used.
        //** NOTE resulting format includes time zone. As a result, datetime returned will always be true, whether or not time the numeric time value reflects local time
        //** NOTE zone will also impact formatting (eg if user overrides in country that uses 24h clock, resulting format will be in 24h)
    const localDateTime = DateTime.fromISO(date).toFormat('ff ZZZZ');
    const overrideDateTime = DateTime.fromISO(date, { zone: zone}).toFormat('ff ZZZZ');

    if (zone) {
        return overrideDateTime;
    } else {
        return localDateTime;
    }
}

export const formatDateForNotification = (date, zone) => {
    const localDateTime = DateTime.fromISO(date).toFormat('ff ZZZZ');
    const overrideDateTime = DateTime.fromISO(date, { zone: zone}).toFormat('ff ZZZZ');

 

    const today = new Date();
    const currentDate = DateTime.fromISO(today.toISOString()).toISODate();
    const notificationDay = DateTime.fromISO(date).toISODate();

    const notificationTime = DateTime.fromISO(date).toFormat('t ZZZZ'); 

    if (currentDate == notificationDay) {
        return notificationTime;
    } else {
        return notificationDay;
    }
}


export const orderArrayByDates = (array, dateKey) => {
    // take in an array of objects that contain each contain a date, return an array with the objects in order of the date
    let newArray = [...array]
    newArray.sort((a,b) => {
        if (a[dateKey] < b[dateKey]) {
            return -1;
        } 
        if (a[dateKey] > b[dateKey]) {
            return 1;
        }
        return 0;
    });
    return newArray;
}


export function useSortableData(items, config = null) {
    // helper function for sorting tables by column. Takes in Array of data objects. Use items.map (instead of Array.map) to create table rows.
    const [sortConfig, setSortConfig] = useState(config);

    const sortedItems = useMemo(() => {
        let sortableItems = [...items];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key] || b[sortConfig.key] === null) {
                    return sortConfig.direction === "ascending" ? -1 : 1;
                } 
                if (a[sortConfig.key] > b[sortConfig.key] || a[sortConfig.key] === null) {
                    return sortConfig.direction === "ascending" ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [items, sortConfig]);
    
    const requestSort = key => {
        let direction = "ascending";
        
        if (
            sortConfig && 
            sortConfig.key === key && 
            sortConfig.direction === "ascending"
        )   {
            direction = "descending";
        }
        setSortConfig({ key, direction });
    }

    return { items: sortedItems, requestSort, sortConfig };
}

// Guards a submit handler against firing again while a previous invocation is
// still in flight (see GitHub #634 -- an unguarded button let repeated clicks
// each fire their own submit, producing duplicate records), and exposes
// isSubmitting so a button can disable itself / show a loading label for the
// duration. Wrap the handler passed to onSubmit/onClick:
//
//   const { isSubmitting, guardedSubmit } = useSubmitGuard();
//   const handleSend = guardedSubmit((e) => {
//       e.preventDefault();
//       return props.someAction(...); // return the promise so this can reset on settle
//   });
//   <button type="submit" disabled={isSubmitting}>{isSubmitting ? "Sending..." : "Send"}</button>
//
// If the wrapped function doesn't return a promise, the guard releases on the
// next tick instead of staying stuck disabled -- still safe, just unable to
// cover that particular call the way a promise-returning one can.
export function useSubmitGuard() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submittingRef = useRef(false);

    const release = useCallback(() => {
        submittingRef.current = false;
        setIsSubmitting(false);
    }, []);

    const guardedSubmit = useCallback((fn) => (...args) => {
        if (submittingRef.current) return undefined;
        submittingRef.current = true;
        setIsSubmitting(true);

        let result;
        try {
            result = fn(...args);
        } catch (err) {
            release();
            throw err;
        }

        if (result && typeof result.finally === "function") {
            result.finally(release);
        } else {
            release();
        }
        return result;
    }, [release]);

    return { isSubmitting, guardedSubmit };
}

export const getClassNamesForThButton = (name, sortConfig) => {
    if (!sortConfig) {
        return;
    }
    return sortConfig.key === name ? sortConfig.direction : undefined;
};

export const alphabetizeByAttribute = (list, attribute) => {
    const newList = list.sort((a, b) => {
        const aAttribute = a[attribute] ? a[attribute] : "";
        const bAttribute = b[attribute] ? b[attribute] : "";
        return aAttribute.localeCompare(bAttribute);
    });
    return newList;
}

export const alphabetizeByAttributeReverse = (list, attribute) => {
    const newList = list.sort((a, b) => {
        const aAttribute = a[attribute] ? a[attribute] : "";
        const bAttribute = b[attribute] ? b[attribute] : "";
        if ( aAttribute < bAttribute || bAttribute === null) {
            return 1;
        } 
        if (aAttribute > bAttribute || bAttribute === null) {
            return -1;
        }
        return 0;
    });
    return newList;
}

export const FormDiv = (props) => {
    if (props.formCondition) {
        return <form onSubmit={(e) => props.onSubmit(e)}>{props.children}</form> 
    } else {
        return <div>{props.children}</div>        
    }
}

export function formatInvoiceStage(stage) {
    switch(stage) {
        case "DR": return "Draft"; 
        case "REV": return  "Review"; 
        case "REJ": return  "Rejected"; 
        case "RED": return  "Revision Draft";
        case "CA": return "Cancelled"; 
        case "PR": return "Payment Required"; 
        case "PU": return "Paid Unconfirmed"; 
        case "PC": return "Paid Confirmed"; 
        case "AI": return "Adjudication Initiated"; 
        case "AC": return "Adjudication Complete";
        default: return "";
    }
}
export function formatInvoiceAction(stage) {
    switch(stage) {
        case "DR": return "Send"; 
        case "REV": return  "Review"; 
        case "REJ": return  "Revise"; 
        case "RED": return  "Send Revision";
        case "CA": return "Cancelled"; 
        case "PR": return "Pay"; 
        case "PU": return "Close"; 
        case "PC": return "Closed"; 
        case "AI": return "Close"; 
        case "AC": return "Closed";
        default: return "";
    }
}
export const getActionColor = (stage, action) => {
    switch (action) {
        case "wait":
            switch(stage) {
            case "REV": 
            case "REJ": 
            case "PR": 
            case "PU": 
            case "AI": 
            case "CA": 
            case "PC": 
            case "AC": 
            default: return "light";
        }
        case "act": 
        switch(stage) {
            case "DR": return "light"
            case "REJ": return "tb-text"
            case "REV": 
            case "PR":  
            case "PU":  
            case "AI": 
            default: return "p-text"
        }
    }
}

export const getActionIcon = (stage, action) => {
    switch (action) {
        case "wait":
            switch(stage) {
            case "REV":
            case "REJ":
            case "PR": 
            case "PU": 
            case "AI": return <IconWait pathClass="g6-fill" height="17px" />
            case "CA": return <IconCancelled pathClass="g7-fill" height="15px" />
            case "PC": return <IconTick pathClass="ta-fill" height="15px" />
            case "AC": return <IconTick pathClass="ta-fill" height="15px" />
            default: return null;
        }
        case "act": 
        switch(stage) {
            case "DR": return <IconCrayon pathClass="g6-fill" height="15px" />
            case "REJ": return <IconWarning pathClass="tb-fill" height="15px" />
            case "REV": 
            case "PR": 
            case "PU": 
            case "AI": 
            default: return <IconIndicator pathClass="p-fill" height="14px" />
        }
    }
}

export const getRoleName = (role) => {
    switch(role) {
        case "PCA": return "Purchaser Rep";
        case "VCA": return "Vendor Rep";
        case "AR": return "Accounts Receivable";
        case "AP": return "Accounts Payable";
        case "APP1": return "Approver 1";
        case "APP2": return "Approver 2";
        // any other approval stage role is a custom template's free-text stage name (e.g.
        // "Manager Approval") -- already human-readable, display as-is
        default: return role;
    }
}
